import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Send, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { getAuthToken } from '../utils/auth';
import { useTheme } from '../context/ThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QuizPage = () => {
  const { dealerSlug } = useParams();
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showEmailCapture, setShowEmailCapture] = useState(true);
  const [email, setEmail] = useState('');
  const [zipcode, setZipcode] = useState('');
  const [isComplete, setIsComplete] = useState(false);
  const [questionCount, setQuestionCount] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(11);
  const [dealer, setDealer] = useState(null);
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  // Auto-focus input after loading completes
  useEffect(() => {
    if (!isLoading && sessionId && !isComplete) {
      inputRef.current?.focus();
    }
  }, [isLoading, sessionId, isComplete, messages]);

  // Fetch dealer info if slug is provided
  useEffect(() => {
    const fetchDealer = async () => {
      if (dealerSlug) {
        try {
          const response = await axios.get(`${API}/dealer/by-slug/${dealerSlug}`);
          setDealer(response.data);
        } catch (error) {
          console.error('Error fetching dealer:', error);
          toast.error('Dealer not found or inactive');
        }
      }
    };
    fetchDealer();
  }, [dealerSlug]);

  const startQuiz = async (userEmail = null, userZipcode = null) => {
    try {
      setIsLoading(true);
      const token = getAuthToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await axios.post(
        `${API}/quiz/start`,
        { email: userEmail, zipcode: userZipcode, dealer_slug: dealerSlug },
        { headers }
      );
      
      setSessionId(response.data.session_id);
      setMessages([{ role: 'assistant', content: response.data.message }]);
      setQuestionCount(1);
      if (response.data.dealer) {
        setDealer(response.data.dealer);
      }
    } catch (error) {
      console.error('Error starting quiz:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to start quiz';
      
      if (errorMessage.includes('Budget') || errorMessage.includes('exceeded')) {
        toast.error('AI service temporarily unavailable. Please contact support or try again later.');
        setShowEmailCapture(true);
      } else {
        toast.error('Failed to start quiz. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }
    if (!zipcode || zipcode.length < 5) {
      toast.error('Please enter a valid 5-digit zipcode');
      return;
    }
    
    // Check if email has already taken the quiz
    try {
      const checkResponse = await axios.get(`${API}/quiz/check-email/${encodeURIComponent(email)}`);
      if (checkResponse.data.quiz_taken) {
        toast.info("You've already taken the quiz! Redirecting to your results...");
        navigate(`/my-matches/${checkResponse.data.session_id}`);
        return;
      }
    } catch (error) {
      // If check fails, proceed anyway (endpoint might not exist yet)
      console.log('Email check skipped:', error);
    }
    
    setShowEmailCapture(false);
    await startQuiz(email, zipcode);
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || !sessionId || isLoading) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    
    try {
      setIsLoading(true);
      const response = await axios.post(`${API}/quiz/message`, {
        session_id: sessionId,
        message: userMessage
      });
      
      setMessages(prev => [...prev, { role: 'assistant', content: response.data.message }]);
      
      if (!response.data.is_complete) {
        setQuestionCount(prev => prev + 1);
      }
      
      if (response.data.is_complete) {
        setIsComplete(true);
        // Don't auto-redirect - let user click button
      }
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to send message';
      
      if (errorMessage.includes('Budget') || errorMessage.includes('exceeded')) {
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: "I'm sorry, but our AI service is temporarily experiencing high demand. Please try again in a few moments, or contact support if the issue persists." 
        }]);
        toast.error('AI service temporarily unavailable. Please try again shortly.');
      } else {
        toast.error('Failed to send message. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className={`min-h-screen py-12 ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`} data-testid="quiz-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8" data-testid="quiz-header">
          {dealer && (
            <p className="text-sm text-[#F2A900] font-semibold mb-2">
              Powered by {dealer.business_name}
            </p>
          )}
          <h1 
            className="text-4xl md:text-5xl font-bold uppercase tracking-tight text-[#004E86] mb-4"
            style={{fontFamily: 'Barlow Condensed, sans-serif'}}
          >
            YOUR RV MATCH A.I. QUIZ
          </h1>
          <p className="text-lg text-gray-700">
            Let's find your perfect RV match! Answer a few questions about your travel style and needs.
          </p>
        </div>

        {/* Text Message Style Chat Container */}
        <div className="bg-gradient-to-b from-gray-100 to-gray-200 rounded-2xl shadow-xl overflow-hidden" data-testid="chat-container">
          {/* Chat Header */}
          <div className="bg-[#004E86] text-white px-4 py-3 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
              <span className="text-lg">🚐</span>
            </div>
            <div>
              <p className="font-semibold">{dealer ? dealer.business_name : 'RV Match AI'}</p>
              <p className="text-xs text-white/70">Online now</p>
            </div>
          </div>
          
          {/* Messages - MyMessage Style */}
          <div className="p-4 space-y-3 max-h-[450px] overflow-y-auto" data-testid="messages-container" style={{backgroundImage: 'linear-gradient(to bottom, #f3f4f6, #e5e7eb)'}}>
            {messages.map((msg, index) => (
              <div
                key={index}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                data-testid={`message-${msg.role}-${index}`}
              >
                <div
                  className={`max-w-[75%] px-4 py-2.5 shadow-sm ${
                    msg.role === 'user'
                      ? 'bg-[#007AFF] text-white rounded-2xl rounded-br-md'
                      : 'bg-white text-gray-900 rounded-2xl rounded-bl-md'
                  }`}
                  style={{
                    boxShadow: msg.role === 'user' 
                      ? '0 1px 2px rgba(0, 122, 255, 0.3)' 
                      : '0 1px 2px rgba(0, 0, 0, 0.1)'
                  }}
                >
                  <p className="text-[15px] leading-relaxed">{msg.content}</p>
                </div>
              </div>
            ))}
            
            {isLoading && (
              <div className="flex justify-start" data-testid="loading-indicator">
                <div className="bg-white rounded-2xl rounded-bl-md px-4 py-3 shadow-sm">
                  <div className="flex gap-1.5">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '0ms'}}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '150ms'}}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '300ms'}}></div>
                  </div>
                </div>
              </div>
            )}
            {/* Auto-scroll anchor */}
            <div ref={messagesEndRef} />
          </div>

          {/* Input - MyMessage Style */}
          {!isComplete && sessionId && (
            <div className="bg-gray-50 border-t border-gray-200 p-3" data-testid="message-input-container">
              <div className="flex gap-2 items-center">
                <div className="flex-1 relative">
                  <Input
                    ref={inputRef}
                    type="text"
                    placeholder="MyMessage"
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    disabled={isLoading}
                    className="rounded-full bg-white border-gray-300 pl-4 pr-12 py-2.5"
                    data-testid="message-input"
                    autoFocus
                  />
                </div>
                <Button
                  onClick={sendMessage}
                  disabled={isLoading || !inputMessage.trim()}
                  className="rounded-full w-10 h-10 p-0 bg-[#007AFF] hover:bg-[#0066CC] text-white"
                  data-testid="send-message-button"
                >
                  <Send size={18} />
                </Button>
              </div>
            </div>
          )}

          {isComplete && (
            <div className="text-center py-6 bg-gray-50 border-t" data-testid="quiz-complete-message">
              <p className="text-gray-600 mb-4">Quiz Complete! Ready to see your results?</p>
              <Button
                onClick={() => navigate(`/my-matches/${sessionId}`)}
                className="bg-[#F2A900] text-black hover:bg-[#D99700] font-bold uppercase tracking-wide px-8 py-3 rounded-full"
                data-testid="get-results-button"
              >
                Unlock My RV Matches
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Email Capture Modal */}
      <Dialog open={showEmailCapture} onOpenChange={setShowEmailCapture}>
        <DialogContent className={isDark ? 'bg-gray-900 text-white border-gray-700' : ''} data-testid="email-capture-modal">
          <DialogHeader>
            <DialogTitle className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-[#004E86]'}`} style={{fontFamily: 'Barlow Condensed, sans-serif'}}>
              Get Started
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEmailSubmit} className="space-y-4">
            <p className={isDark ? 'text-gray-300' : 'text-gray-700'}>
              Enter your email and zipcode to receive your personalized RV recommendations.
            </p>
            <Input
              type="email"
              placeholder="your@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className={isDark ? 'bg-gray-800 border-gray-700 text-white placeholder:text-gray-500' : ''}
              data-testid="email-input"
            />
            <Input
              type="text"
              placeholder="Zipcode (e.g., 78701)"
              value={zipcode}
              onChange={(e) => setZipcode(e.target.value)}
              required
              maxLength={5}
              className={isDark ? 'bg-gray-800 border-gray-700 text-white placeholder:text-gray-500' : ''}
              data-testid="zipcode-input"
            />
            <Button
              type="submit"
              className="w-full bg-[#F2A900] text-black hover:bg-[#D99700] font-bold uppercase tracking-wide py-3"
              data-testid="email-submit-button"
            >
              Start Quiz
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default QuizPage;
